import React, { useState, useEffect, FC, ChangeEvent } from 'react'
import { Controller, ControllerProps } from 'react-hook-form'
import debounce from 'lodash/debounce'
import { TextArea, ITextAreaProps } from 'transfix-ui/components/TextArea'

type IBaseControllerProps = ControllerProps<React.ComponentType<ITextAreaProps>>

interface ITextAreaWrapperProps extends ITextAreaProps {}

/**
 * This is only used in the `<ControlledTextArea />` component to
 * allow for debounced value updates.
 * @note This component is not exported because it should not be used anywhere else
 */
const TextAreaWrapper: FC<ITextAreaWrapperProps> = ({ onChange, value = '', ...rest }) => {
  const [inputValue, setInputValue] = useState<string>(value)

  // Update state when receiving new props
  useEffect(() => setInputValue(value), [value])

  /**
   * This mimics `<TextInput />`'s controlled state updates,
   * which sets an internal state, but also sends an identical
   * change to parent component's state
   */
  const changeHandler = (e: ChangeEvent<HTMLTextAreaElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const { value: eventValue } = e.target
    setInputValue(eventValue)
    if (onChange) {
      onChange(e)
    }
  }

  return (
    <TextArea
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
      value={inputValue}
      onChange={changeHandler}
    />
  )
}

export interface IControlledTextAreaProps
  extends Pick<IBaseControllerProps, 'control' | 'name' | 'defaultValue'> {
  /**
   * Adds a slight delay to updating the values in `control` object
   * This significantly improves performance on large forms, and forms
   * that use `useFormContext` hook
   * @default 150
   */
  debounceMs?: number
  TextAreaProps?: Partial<ITextAreaProps>
}

/**
 * Helper component for use with React Hook Form
 * @see https://react-hook-form.com/api#Controller
 * @example
 * ```
 * <ControlledTextArea
 *   name='pickupNumber'
 *   control={control}
 *   TextAreaProps={{
 *     label: 'Pickup Number',
 *     placeholder: '1234',
 *   }}
 * />
 * ```
 */
export const ControlledTextArea: FC<IControlledTextAreaProps> = ({
  control,
  name,
  defaultValue,
  TextAreaProps = {},
  debounceMs = 150,
}) => {
  const {
    ariaLabel,
    dataTest,
    disabled = false,
    invalid = false,
    label,
    placeholder = '',
    required = false,
    size = 'large',
    toolTipInfo,
  } = TextAreaProps || {}
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ onBlur, onChange, value }) => {
        const changeHandler = debounce((val: string) => {
          onChange(val)
        }, debounceMs)

        return (
          <TextAreaWrapper
            value={value}
            /**
             * The following onChange construction is necessary to pass `changeHandler`
             * a value before the `event` value is nullified
             */
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => changeHandler(e.target.value)}
            size={size}
            placeholder={placeholder}
            disabled={disabled}
            label={label}
            dataTest={dataTest}
            onBlur={onBlur}
            required={required}
            invalid={invalid}
            ariaLabel={ariaLabel}
            toolTipInfo={toolTipInfo}
          />
        )
      }}
    />
  )
}

export default ControlledTextArea
